import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { setIsPaused } from '../../../redux/actions/player-actions';
import { DocumentType } from '../../../types/Podcast';
import Button from '../../../components/ui/button/Button';
import FlexBox from '../../../components/ui/flexbox/FlexBox';
import Image from '../../../components/ui/image/Image';
import {
  getDocumentProgress,
  getSummaryDocumentProgress,
  isDocumentPlaying,
  isSummaryDocumentPlaying
} from '../utils/utils';
import { ReactComponent as DocumentIcon } from '../images/icon-document.svg';
import DocumentActions from './DocumentActions';
import NewLabel from './NewLabel';
import ShowMoreText from './ShowMoreText';

interface DocumentBoxProps {
  $isActive: boolean;
}

const DocumentBox = styled(FlexBox)<DocumentBoxProps>`
  padding: 20px;
  margin-top: 2px;
  background-color: ${(props) =>
    props.$isActive
      ? 'var(--color-oxford-blue-rgba)'
      : 'var(--color-oxford-blue)'};
  ${(props) =>
    props.$isActive ? 'box-shadow: rgba(0, 0, 0, 0.55) 0px 5px 15px' : ''};

  &:hover {
    background-color: var(--color-oxford-blue-rgba);
    box-shadow: rgba(0, 0, 0, 0.55) 0px 5px 15px;
  }
`;

const DocumentMedia = styled.div`
  flex-basis: 0;
  position: relative;
`;

const DocumentDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 0 10px;
`;

const DocumentName = styled.h2`
  font-size: 16px;
  color: var(--color-light-gray);
  white-space: normal;
  word-wrap: break-word;
`;

const ReleaseDate = styled.h6`
  font-size: 12px;
  color: var(--color-deep-gray);
  margin-top: 16px;
`;

const Description = styled.div`
  font-size: 14px;
  color: var(--color-light-gray);
  max-width: 600px;
  margin-top: 6px;
`;

const Actions = styled.div`
  width: 250px;
`;

const DocumentButton = styled(Button)`
  width: 100%;
  color: var(--color-light-purple);
  background-color: var(--color-ai-green);
  font-size: 16px;
  margin-bottom: 20px;

  &:hover {
    background-color: var(--color-ai-green-hover);
  }

  &:disabled {
    background-color: var(--color-dark-gray);
    color: var(--color-gray);

    & svg {
      stroke: var(--color-gray);
    }
  }

  & svg {
    stroke: var(--color-ai-green-hover);
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 10px;
  gap: 12px;
`;

interface DocumentProps {
  document: DocumentType;
  onGoToDocument?: (document: DocumentType) => void;
  onPlaySummary: (document: DocumentType) => void;
  onPlayDocument: (document: DocumentType) => void;
  onAddSummaryToPlaylist: (document: DocumentType) => void;
  onAdddocumentoPlaylist: (document: DocumentType) => void;
  isPaused: boolean;
  setIsPaused: (isPaused: boolean) => void;
  playerDocument: DocumentType | '';
}

const Document: React.FC<DocumentProps> = ({
  document,
  onGoToDocument,
  onPlaySummary,
  onPlayDocument,
  onAdddocumentoPlaylist,
  isPaused,
  setIsPaused,
  playerDocument
}) => {
  const hasSummary = document.isSummary || document.summaryDocumentId !== null;
  const availableSoonDocument =
    document.isSummary && document.summaryDocumentId === null;

  const isPlayingDocument = isDocumentPlaying(playerDocument, document, isPaused);
  const documentProgress = getDocumentProgress(playerDocument, document);

  const isPlayingSummaryDocument = isSummaryDocumentPlaying(
    playerDocument,
    document.summaryDocument,
    isPaused
  );
  const summaryDocumentProgress = getSummaryDocumentProgress(
    playerDocument,
    document.summaryDocument
  );

  return (
    <DocumentBox $isActive={isPlayingDocument || isPlayingSummaryDocument}>
      <DocumentMedia>
        {document.isNew && (
          <NewLabel>
            <span>New</span>
          </NewLabel>
        )}
        {/* <Image width="50" height="50" src={document.imgUrl} /> */}
      </DocumentMedia>
      <DocumentDetails>
        <DocumentName>{document.episodeName}</DocumentName>
        <ReleaseDate>{document.releaseDate}</ReleaseDate>
        <Description>
          <ShowMoreText lines={3}>{document.description}</ShowMoreText>
        </Description>
      </DocumentDetails>
      <Actions>
        {onGoToDocument && (
          <DocumentButton
            onClick={() => onGoToDocument(document)}
            disabled={
              document.documentUrl === null || document.documentUrl === ''
            }
          >
            <DocumentIcon />
            Go to document
          </DocumentButton>
        )}
        <Wrapper>
          {hasSummary && (
            <DocumentActions
              text="Summary"
              onPlay={() => onPlaySummary(document)}
              onPause={() => setIsPaused(true)}
              isPlaying={isPlayingSummaryDocument}
              progress={summaryDocumentProgress}
            />
          )}
          <DocumentActions
            text="Full Document"
            onPlay={() => onPlayDocument(document)}
            onPause={() => setIsPaused(true)}
            onAddToPlaylist={() => onAdddocumentoPlaylist(document)}
            isPlaying={isPlayingDocument}
            progress={documentProgress}
            disabled={availableSoonDocument}
          />
        </Wrapper>
      </Actions>
    </DocumentBox>
  );
};

const mapStateToProps = (store) => {
  return {
    isPaused: store.player.isPaused,
    playerDocument: store.player.playerDocument
  };
};

const mapDispatchToProps = {
  setIsPaused
};

export default connect(mapStateToProps, mapDispatchToProps)(Document);
